import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { hasDecided, setConsent } from '../lib/analytics';

/** Footer and anywhere else can dispatch this to let a visitor change their mind. */
export const REOPEN_CONSENT_EVENT = 'synapsis:reopen-consent';

/**
 * The consent banner — now load-bearing.
 *
 * It used to write a value to localStorage that nothing ever read, so
 * "Reject" changed nothing and analytics ran regardless. The decision now
 * actually starts or stops PostHog through src/lib/analytics.js, and it can be
 * revisited: consent you cannot withdraw is not consent.
 */
const CookieConsent = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!hasDecided()) {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const reopen = () => setIsVisible(true);
    window.addEventListener(REOPEN_CONSENT_EVENT, reopen);
    return () => window.removeEventListener(REOPEN_CONSENT_EVENT, reopen);
  }, []);

  const decide = useCallback((decision) => {
    setConsent(decision);
    setIsVisible(false);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
          role="dialog"
          aria-live="polite"
          aria-label={t('cookie.message')}
        >
          <div className="max-w-4xl mx-auto surface rounded-2xl p-4 md:p-6 shadow-2xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <p className="text-sm text-gray-300 flex-1">
                {t('cookie.message')}{' '}
                <Link to="/cookie-policy" className="text-accent-400 hover:text-accent-300 underline">
                  {t('cookie.link')}
                </Link>.
              </p>
              <div className="flex gap-3 shrink-0">
                <button
                  onClick={() => decide('rejected')}
                  className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white border border-white/20 hover:border-white/40 rounded-lg transition-colors"
                >
                  {t('cookie.reject')}
                </button>
                <button
                  onClick={() => decide('accepted')}
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-500 hover:bg-accent-600 rounded-lg transition-colors"
                >
                  {t('cookie.accept')}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
